import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Single screen: shows 4 questions sequentially. If user fails any, show message and stop.
// If all pass, capture current retinoid + dose and show next-step recommendation.
const RETINOID_TYPES = [
  { key: "retinyl_palmitate", label: "Retinyl palmitate (or other retinyl ester)" },
  { key: "retinol", label: "Retinol" },
  { key: "retinaldehyde", label: "Retinaldehyde" },
  { key: "tretinoin", label: "Tretinoin (prescription)" },
  { key: "tazarotene", label: "Tazarotene (prescription)" },
  { key: "adapalene", label: "Adapalene (prescription)" },
];

export default function RetinoidAugmentation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q1, setQ1] = useState(""); const [q2, setQ2] = useState(""); const [q3, setQ3] = useState(""); const [q4, setQ4] = useState("");
  const [retType, setRetType] = useState(""); const [dose, setDose] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  // Determine current visible step
  const step = result ? "result"
    : !q1 ? "q1"
    : q1 !== "every_evening" ? "block"
    : !q2 ? "q2"
    : q2 === "no" ? "block"
    : !q3 ? "q3"
    : q3 === "yes" ? "block"
    : !q4 ? "q4"
    : q4 === "yes" ? "block"
    : "retinoid_input";

  const submitFinal = async () => {
    if (!retType) return setErr("Please choose your current retinoid.");
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/retinoid-augmentation", {
        q1, q2, q3, q4,
        current_retinoid_type: retType,
        current_dose_pct: dose === "" ? null : Number(dose),
      });
      setResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  // Generate block messages from server response would be ideal, but we use static here
  const blockMessage = q1 && q1 !== "every_evening"
    ? "Before increasing the potency of your retinoid, ensure that you are using and tolerating your retinoid consistently on a nightly basis for at least 8 weeks.\n\nOnce you've been using your retinoid every evening for at least 8 weeks, feel free to revisit this questionnaire and try again.\n\nP.S. — totally OK to miss a night here and there, but you should not increase your retinoid potency if you are skipping nights due to dryness or irritation."
    : "If you are experiencing excessive dryness, peeling, redness, stinging, itching or burning, please consult with your healthcare provider.\n\nIn some mild cases, your skin may need a bit more time to adapt. In other cases, you may need to step down your retinoid potency or increase skin barrier support. We recommend you consult your healthcare provider if you continue to experience symptoms.";

  const Pill = ({ active, onClick, children, ...rest }) => (
    <button type="button" onClick={onClick} {...rest}
      className={`aa-pill px-5 py-2 mr-2 mb-2 ${active ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}>
      {children}
    </button>
  );

  return (
    <div className="App" data-testid="page-retinoid-augmentation">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Retinoid augmentation</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Ready to level up your <span className="serif-italic">retinoid</span>?</h1>
        <div className="aa-card p-4 mt-6 verdict-yellow" data-testid="retinoid-stop">
          <strong>STOP:</strong> Enter only after you have used your current retinoid every evening for at least 8 weeks.
        </div>

        {/* Q1 */}
        {(step === "q1" || ["q2","q3","q4","retinoid_input","result"].includes(step)) && (
          <Question num={1} text="How often are you using your retinoid?" testid="ret-q1">
            <Pill data-testid="ret-q1-a" active={q1==="every_evening"} onClick={() => setQ1("every_evening")}>Every evening</Pill>
            <Pill data-testid="ret-q1-b" active={q1==="every_other"} onClick={() => setQ1("every_other")}>Every other evening</Pill>
            <Pill data-testid="ret-q1-c" active={q1==="less"} onClick={() => setQ1("less")}>Less than that</Pill>
          </Question>
        )}

        {/* Q2 */}
        {(step === "q2" || ["q3","q4","retinoid_input","result"].includes(step)) && (
          <Question num={2} text="Do your face, neck and chest feel hydrated (not dry or tight)?" testid="ret-q2">
            <Pill data-testid="ret-q2-a" active={q2==="yes"} onClick={() => setQ2("yes")}>Yes</Pill>
            <Pill data-testid="ret-q2-b" active={q2==="most"} onClick={() => setQ2("most")}>Most of the time</Pill>
            <Pill data-testid="ret-q2-c" active={q2==="no"} onClick={() => setQ2("no")}>No</Pill>
          </Question>
        )}

        {/* Q3 */}
        {(step === "q3" || ["q4","retinoid_input","result"].includes(step)) && (
          <Question num={3} text="Do you have any peeling or flaking on your face, neck or chest?" testid="ret-q3">
            <Pill data-testid="ret-q3-a" active={q3==="yes"} onClick={() => setQ3("yes")}>Yes</Pill>
            <Pill data-testid="ret-q3-b" active={q3==="no"} onClick={() => setQ3("no")}>No</Pill>
          </Question>
        )}

        {/* Q4 */}
        {(step === "q4" || ["retinoid_input","result"].includes(step)) && (
          <Question num={4} text="Are you experiencing any irritation — redness in response to products, stinging, itching, or burning?" testid="ret-q4">
            <Pill data-testid="ret-q4-a" active={q4==="yes"} onClick={() => setQ4("yes")}>Yes</Pill>
            <Pill data-testid="ret-q4-b" active={q4==="no"} onClick={() => setQ4("no")}>No</Pill>
          </Question>
        )}

        {step === "block" && (
          <div className="aa-card p-7 mt-7 fade-in" data-testid="ret-block-msg">
            <div className="aa-eyebrow mb-3">Hold for now</div>
            <p className="text-[var(--aa-text-2)] whitespace-pre-line">{blockMessage}</p>
          </div>
        )}

        {step === "retinoid_input" && (
          <div className="aa-card p-7 mt-7 fade-in" data-testid="ret-input-card">
            <div className="aa-eyebrow mb-3">Tell us your current retinoid</div>
            <div className="space-y-5">
              <div>
                <label className="aa-eyebrow block mb-2">Current retinoid</label>
                <select data-testid="ret-type" value={retType} onChange={(e) => setRetType(e.target.value)} className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3">
                  <option value="">Select…</option>
                  {RETINOID_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
              {(retType === "retinol" || retType === "retinaldehyde" || retType === "tretinoin" || retType === "tazarotene") && (
                <div data-testid="ret-input">
                  <label className="aa-eyebrow block mb-2">Current dose (%)</label>
                  <input data-testid="ret-dose" type="number" step="0.01" min="0" value={dose} onChange={(e) => setDose(e.target.value)} placeholder="e.g. 0.5 (for 0.5% retinol)" className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3" />
                </div>
              )}
              {err && <div className="text-sm text-[var(--aa-terra)]" data-testid="ret-error">{err}</div>}
              <button data-testid="ret-submit" onClick={submitFinal} disabled={loading} className="aa-cta-primary">
                {loading ? "Calculating…" : "Show me my next step →"}
              </button>
            </div>
          </div>
        )}

        {step === "result" && result?.ready && (
          <div className="aa-card p-7 mt-7 fade-in space-y-5" data-testid="ret-result">
            <span className="aa-pill verdict-green">Ready to level up</span>
            <p className="text-[var(--aa-text-2)]">{result.message}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="aa-card p-5">
                <div className="aa-eyebrow mb-2">Currently using</div>
                <div className="font-display text-lg">{result.current?.label}</div>
              </div>
              <div className="aa-card p-5 verdict-green">
                <div className="aa-eyebrow mb-2">{result.at_top ? "Top of the ladder" : "Next step"}</div>
                <div className="font-display text-lg">{result.at_top ? "You're at the most potent tier." : result.next?.label}</div>
                {result.next?.examples?.[0] && <div className="text-sm text-[var(--aa-text-3)] mt-1 leading-relaxed">e.g. {result.next.examples[0]}</div>}
              </div>
            </div>

            {!result.at_top && result.next_product && (
              <div className="aa-card p-6" data-testid="ret-next-product">
                <div className="aa-eyebrow mb-2">{result.next_product.kind === "rx" ? "Talk to your dermatologist about" : "Suggested product"}</div>
                {result.next_product.kind === "otc" ? (
                  <>
                    {result.next_product.url ? (
                      <a href={result.next_product.url} target="_blank" rel="noopener noreferrer" className="font-display text-xl hover:underline underline-offset-4">{result.next_product.product}</a>
                    ) : (
                      <div className="font-display text-xl">{result.next_product.product}</div>
                    )}
                    {result.next_product.note && <p className="mt-2 text-sm text-[var(--aa-text-2)] leading-relaxed">{result.next_product.note}</p>}
                    {result.next_product.alt && (
                      <div className="mt-3 text-sm text-[var(--aa-text-2)] leading-relaxed">
                        Then step up to:{" "}
                        {result.next_product.alt_url ? (
                          <a href={result.next_product.alt_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{result.next_product.alt}</a>
                        ) : (
                          <span>{result.next_product.alt}</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-display text-xl"><em>{result.next_product.generic}</em></div>
                    {result.next_product.note && <p className="mt-2 text-sm text-[var(--aa-text-2)] leading-relaxed">{result.next_product.note}</p>}
                  </>
                )}
              </div>
            )}

            <div className="aa-card p-5">
              <div className="aa-eyebrow mb-2">How to introduce it</div>
              <p className="text-sm text-[var(--aa-text-2)] leading-relaxed">{result.introduction_protocol}</p>
            </div>
            <div className="aa-card p-5 verdict-yellow">
              <div className="aa-eyebrow mb-2">Watch for</div>
              <p className="text-sm leading-relaxed">{result.watch_for}</p>
            </div>
            <button data-testid="ret-back" onClick={() => nav("/dashboard")} className="aa-cta-secondary">← Back to dashboard</button>
          </div>
        )}
      </section>
    </div>
  );
}

function Question({ num, text, children, testid }) {
  return (
    <div className="aa-card p-6 mt-6" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="aa-eyebrow w-8 mt-1">{String(num).padStart(2, "0")}</span>
        <div className="flex-1">
          <div className="text-[var(--aa-text)] mb-3">{text}</div>
          <div className="flex flex-wrap">{children}</div>
        </div>
      </div>
    </div>
  );
}
